import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";
import { useEffect } from "react";

import { VCS_INTEGRATION_DETAILS_UNION } from "constants/vcsProviders";
import { BaseSelectItem } from "ds/components/Select/types";
import useTypedContext from "hooks/useTypedContext";
import { VcsIntegration, VcsProvider } from "types/generated";
import { AccountContext } from "views/AccountWrapper";

import { QUERY_ATTACHABLE_VCS_INTEGRATIONS } from "./gql";
import { AttachableVCSIntegrationsOption, SelectedVcsIntegration } from "./types";

type UseQueryAttachableVCSIntegrationsOptionsProps = {
  provider: VcsProvider;
  spaceId: string;
  onSelect: (vcsIntegration: SelectedVcsIntegration) => void;
  initialValue?: string;
  editMode?: boolean;
};

const useQueryAttachableVCSIntegrationsOptions = ({
  provider,
  spaceId,
  onSelect,
  initialValue,
  editMode,
}: UseQueryAttachableVCSIntegrationsOptionsProps) => {
  const { viewer } = useTypedContext(AccountContext);

  const { data, loading } = useQuery<{
    attachableVCSIntegrationsForProviderInSpace: VcsIntegration[];
  }>(QUERY_ATTACHABLE_VCS_INTEGRATIONS, {
    variables: {
      spaceId,
      provider,
    },
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (!data?.attachableVCSIntegrationsForProviderInSpace || editMode) {
      return;
    }

    // If initialValue is provided and exists in the list, select it
    if (initialValue) {
      const initialValueInList = data.attachableVCSIntegrationsForProviderInSpace.find(
        (item) => item?.id === initialValue
      );

      if (initialValueInList) {
        onSelect({
          id: initialValueInList.id,
          name: initialValueInList.name,
          useGitCheckout: initialValueInList.details?.useGitCheckout,
        });
        return;
      }
    }

    // If no initialValue or initialValue not found, auto-select first integration
    if (data.attachableVCSIntegrationsForProviderInSpace[0]) {
      onSelect({
        id: data.attachableVCSIntegrationsForProviderInSpace[0].id,
        name: data.attachableVCSIntegrationsForProviderInSpace[0].name,
        useGitCheckout:
          data.attachableVCSIntegrationsForProviderInSpace[0].details?.useGitCheckout,
      });
    }
  }, [initialValue, data?.attachableVCSIntegrationsForProviderInSpace, onSelect, editMode]);

  const options = useMemo<(AttachableVCSIntegrationsOption | BaseSelectItem)[]>(() => {
    if (provider === VcsProvider.GithubEnterprise) {
      return (data?.attachableVCSIntegrationsForProviderInSpace || []).map((item) => {
        if (
          viewer.admin &&
          item.details?.__typename === VCS_INTEGRATION_DETAILS_UNION.GITHUB_ENTERPRISE_INTEGRATION
        ) {
          return {
            label: item.name,
            value: item.id,
            hasInstallations: item.details.installations?.length > 0,
            useGitCheckout: item.details?.useGitCheckout,
          };
        }

        return {
          label: item.name,
          value: item.id,
          useGitCheckout: item.details?.useGitCheckout,
        };
      });
    }

    return (data?.attachableVCSIntegrationsForProviderInSpace || []).map((item) => ({
      label: item.name,
      value: item.id,
      useGitCheckout: item.details?.useGitCheckout,
    }));
  }, [data?.attachableVCSIntegrationsForProviderInSpace, provider, viewer.admin]);

  return {
    options,
    loading,
  };
};

export default useQueryAttachableVCSIntegrationsOptions;
